import React, { useEffect, forwardRef, useImperativeHandle } from 'react';
import { FormProvider, SubmitErrorHandler, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useWatch } from 'react-hook-form';
import { QueryObserverResult } from 'react-query';
import { useToast } from '@common/toast';
import { CareerStateType } from '@components/profile/profileOverlay/overlays/career';
import Company from '@components/profile/profileOverlay/overlays/career/careerForm/Company';
import JobGroup from '@components/profile/profileOverlay/overlays/career/careerForm/JobGroup';
import ContractType from '@components/profile/profileOverlay/overlays/career/careerForm/ContractType';
import Role from '@components/profile/profileOverlay/overlays/career/careerForm/Role';
import Performance from '@components/profile/profileOverlay/overlays/career/careerForm/Performance';
import careerFormSchema from '@components/profile/profileOverlay/overlays/career/careerForm/careerFormSchema';
import StartDate from '@components/profile/profileOverlay/overlays/career/careerForm/StartDate';
import EndDate from '@components/profile/profileOverlay/overlays/career/careerForm/EndDate';
import EmploymentStatus from '@components/profile/profileOverlay/overlays/career/careerForm/CompanyType';
import JobTitle from '@components/profile/profileOverlay/overlays/career/careerForm/JobTitle';
import { usePostProfileCareer, usePutProfileCareer } from '@repository/profiles/useProfileCareer';
import Tip from '@components/profile/profileOverlay/overlays/career/careerForm/Tip';
import WriteText from '@components/profile/profileOverlay/overlays/career/careerForm/WriteText';
import { useMatchProfile } from '@repository/match';
import { useInsightAutoSaved } from '@repository/insight/useInsight';
import { ProfileCareersFormVO } from '@domain/vo/profile/ProfileCareersFormVO';
import ProfileCareersVO, { CareersVO } from '@domain/vo/profile/ProfileCareersVO';
import { KeyboardEventUtil } from '@utils/KeyboardEventUtil';

import classnames from 'classnames/bind';
import styles from '../career.module.scss';
const cx = classnames.bind(styles);

const careerDefaultValues: ProfileCareersFormVO = {
  sn: null,
  company: null,
  employmentStatus: null,
  startDate: '',
  endDate: '',
  jobGroup: null,
  jobTitle: null,
  employmentType: null,
  performance: '',
  role: '',
  verified: null,
};

interface CareerFormProps {
  refetch: () => Promise<QueryObserverResult>;
  profileCareer?: ProfileCareersVO | undefined;
  career: CareersVO | null;
  careerIndex: number | null;
  setCareerState: (careerState: CareerStateType) => void;
  openExitModal: () => void;
}

const CareerForm = forwardRef(
  ({ refetch, profileCareer, career, careerIndex, setCareerState, openExitModal }: CareerFormProps, ref) => {
    const Toast = useToast();
    useImperativeHandle(ref, () => ({
      getIsDirty,
    }));

    const { refetch: matchProfileRefetch } = useMatchProfile();
    const methods = useForm<ProfileCareersFormVO>({
      mode: 'onChange',
      defaultValues: careerDefaultValues as ProfileCareersFormVO,
      resolver: yupResolver(careerFormSchema) as any,
    });

    const { handleSubmit, control, reset, formState, getValues } = methods;
    const employmentStatus = useWatch<any>({ name: 'employmentStatus', control });
    const jobGroup = useWatch<any>({ name: 'jobGroup', control });
    const verified = useWatch<any>({ name: 'verified', control });
    const type = verified ? 'verified' : 'required';
    const { data: insightAutoSaved, refetch: insightAutoSavedRefetch } = useInsightAutoSaved({
      enabled: true,
    });

    // 경력 정보 등록
    const { mutateAsync: postProfileCareer } = usePostProfileCareer();

    // 경력 정보 수정
    const { mutateAsync: putProfileCareer } = usePutProfileCareer();

    useEffect(() => {
      if (career) reset(ProfileCareersFormVO.convertProfileCareerFormVO(career));
    }, [career]);

    const getIsDirty = (): boolean => {
      return formState.isDirty;
    };

    const handlePrev = () => {
      if (career && formState.isDirty) {
        openExitModal();
        return;
      }

      if (formState.isDirty) {
        openExitModal();
        return;
      }

      setCareerState('LIST');
    };

    const onSubmit: SubmitHandler<ProfileCareersFormVO> = async (vo) => {
      const profileCareer = ProfileCareersFormVO.convertProfileCareerSaveVO(vo);
      if (vo.sn) {
        await putProfileCareer(
          {
            sn: vo.sn,
            rq: { profileCareer },
          },
          {
            onSuccess: () => {
              if (insightAutoSaved?.auditCount) insightAutoSavedRefetch();
            },
          },
        );
      } else {
        await postProfileCareer({ profileCareer });
      }

      await refetch().then(() => {
        Toast({ type: 'success', iconType: 'info', content: '성공적으로 저장되었습니다.' });
        setCareerState('LIST');
        matchProfileRefetch();
      });
    };

    const onSubmitError: SubmitErrorHandler<ProfileCareersFormVO> = (e) => {
      Toast({ type: 'error', iconType: 'info', content: '저장에 필요한 정보를 작성해주세요.' });
    };

    return (
      <FormProvider {...methods}>
        <form
          id="careerForm"
          className={cx('scrollArea')}
          onSubmit={(v) => handleSubmit(onSubmit, onSubmitError)(v)}
          onKeyDown={KeyboardEventUtil.onCheckKeyDownPreventEnterExceptTextarea}
        >
          <div className={cx('scrollInner')}>
            <div className={cx('scrollWrap')}>
              <div className={cx('writeArea')}>
                <Tip type={type} />

                <div className={cx('writeInner')}>
                  <WriteText type={type} text={'직장명'} />
                  {/* 직장명 */}
                  <Company name="company" readonly={verified} />
                </div>

                <div className={cx('writeInner')}>
                  <WriteText type={type} text={'상태'} />
                  {/* 상태 */}
                  <EmploymentStatus name="employmentStatus" readonly={verified} />
                </div>

                <div className={cx('writeInner')}>
                  <WriteText type={type} text={'근무 기간'} />
                  {/* 근무 기간 */}
                  <div className={cx('periodArea')}>
                    <StartDate name="startDate" readonly={verified} />
                    <mark>~</mark>
                    <EndDate name="endDate" employmentStatus={employmentStatus} readonly={verified} />
                  </div>
                </div>

                <div className={cx('writeInner')}>
                  <span className={cx('writeText', 'required')}>직군</span>
                  {/* 직군 */}
                  <JobGroup name="jobGroup" />
                </div>

                <div className={cx('writeInner')}>
                  <span className={cx('writeText', 'required')}>직무</span>
                  {/* 직무 */}
                  <JobTitle name="jobTitle" jobGroup={jobGroup} />
                </div>

                <div className={cx('writeInner')}>
                  <span className={cx('writeText', 'required')}>계약 형태</span>
                  {/* 계약 형태 */}
                  <ContractType name="employmentType" />
                </div>

                <div className={cx('writeInner')}>
                  <span className={cx('writeText')}>역할</span>
                  {/* 역할 */}
                  <Role name="role" />
                </div>

                <div className={cx('writeInner')}>
                  <span className={cx('writeText')}>주요 성과</span>
                  {/* 주요 성과 */}
                  <Performance name="performance" />
                </div>
              </div>
            </div>
          </div>
        </form>

        <div className={cx('btnArea')}>
          <a className={cx('btn', 'cancel')} role="button" onClick={handlePrev}>
            이전
          </a>
          <button className={cx('btn', 'confirm')} type="submit" form="careerForm">
            등록
          </button>
        </div>
      </FormProvider>
    );
  },
);

export default CareerForm;
